if (!customElements.get('buy-with-credits')) {
  customElements.define(
    'buy-with-credits',
    class BuyWithCredits extends HTMLElement {
      constructor() {
        super();

        this.button = this.querySelector('[data-credits-button]');
        this.dialog = this.querySelector('[data-credits-dialog]');
        this.cancelBtn = this.querySelector('[data-dialog-cancel]');
        this.confirmBtn = this.querySelector('[data-dialog-confirm]');
        this.dialogMessage = this.querySelector('[data-dialog-message]');

        this.closeBtn = this.querySelector('[data-dialog-close]');
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
      }

      connectedCallback() {
        if (!this.button || !this.dialog) {
          console.log('[Buy with Credits] Required elements not found');
          return;
        }

        this.variantId = this.dataset.variantId;
        this.productFilter = this.dataset.productFilter || '';
        this.productId = this.dataset.productId;

        // Credits charged for this variant. Mirrors the backend fallback of 1.
        const parsedCost = parseInt(this.dataset.creditCost, 10);
        this.creditCost = Number.isFinite(parsedCost) && parsedCost > 0 ? parsedCost : 1;

        this.digitalProductsUrl = this.dataset.digitalProductsUrl || '/account';

        // Load i18n strings
        this.i18n = {
          creditSingular: this.dataset.i18nCreditSingular || 'credit',
          creditPlural: this.dataset.i18nCreditPlural || 'credits',
          processing: this.dataset.i18nProcessing || 'Processing...',
          confirmMessage:
            this.dataset.i18nConfirmMessage ||
            'Use {cost} {credit} to get this product? You have {count} {credits} available.',
          errorGeneric:
            this.dataset.i18nErrorGeneric || 'An error occurred while processing your request. Please try again.',
          errorRedeem: this.dataset.i18nErrorRedeem || 'Failed to redeem credits. Please try again.',
          successMessage:
            this.dataset.i18nSuccessMessage ||
            'Success! You can find this product in the My Account section under {link}.',
          successLinkText: this.dataset.i18nSuccessLinkText || 'My Digital Products',
          successClose: this.dataset.i18nSuccessClose || 'Close',
        };

        try {
          const parsed = JSON.parse(this.dataset.customerCredits || '[]');
          // The metafield holds either a single credit object or an array of them.
          this.customerCredits = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          console.error('[Buy with Credits] Failed to parse customer credits:', e);
          this.customerCredits = [];
        }

        this.totalCredits = Number(this.dataset.totalCredits || 0);

        if (!this.totalCredits && Array.isArray(this.customerCredits)) {
          const todayYmd = this.getTodayYmd();

          this.totalCredits = this.customerCredits.reduce((sum, entry) => {
            const c = Number(entry?.credits || 0);
            if (!Number.isFinite(c) || c <= 0) return sum;

            const expiresAt = entry?.expires_at;
            if (!expiresAt) return sum + c;

            const exp = this.expiresYmd(expiresAt);
            if (!exp) return sum + c;
            return exp >= todayYmd ? sum + c : sum;
          }, 0);
        }

        console.log('[Buy with Credits] Initialized', {
          variantId: this.variantId,
          productFilter: this.productFilter,
          productId: this.productId,
          customerCredits: this.customerCredits,
        });

        // Liquid already worked this out server-side; recompute only if it is absent.
        const serverEligible = parseInt(this.dataset.eligibleCredits, 10);
        this.eligibleCredits = Number.isFinite(serverEligible)
          ? serverEligible
          : this.sumEligibleCredits(this.customerCredits);

        if (this.eligibleCredits < this.creditCost) {
          console.log('[Buy with Credits] Not enough eligible credits', {
            eligible: this.eligibleCredits,
            required: this.creditCost,
          });
          this.style.display = 'none';
          return;
        }

        this.button.addEventListener('click', this.handleButtonClick.bind(this));
        this.cancelBtn.addEventListener('click', this.handleCancel.bind(this));
        this.confirmBtn.addEventListener('click', this.handleConfirm.bind(this));
        this.closeBtn?.addEventListener('click', () => this.dialog.close());

        // Close on backdrop click (outside modal content)
        this.dialog.addEventListener('click', this.handleBackdropClick);
      }

      ymd(dateObj) {
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        return Number(`${yyyy}${mm}${dd}`);
      }

      expiresYmd(expiresAt) {
        const m = String(expiresAt).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return null;
        return Number(`${m[1]}${m[2]}${m[3]}`);
      }

      getTodayYmd() {
        return this.ymd(new Date());
      }


      /**
       * Total credits that can pay for this product: non-expired entries whose filter
       * matches the product, plus unrestricted entries. The backend drains these
       * together, so the sum is what decides whether the customer can redeem.
       */
      sumEligibleCredits(customerCredits) {
        const todayYmd = this.getTodayYmd();

        const isValid = (credit) => {
          if (!credit || Number(credit.credits) < 1) return false;

          const expiresAt = credit.expires_at;
          if (!expiresAt) return true;

          const exp = this.expiresYmd(expiresAt);
          if (!exp) return true;
          return exp >= todayYmd;
        };

        return customerCredits.reduce((sum, credit) => {
          if (!isValid(credit)) return sum;

          const filter = credit.filter || '';
          if (filter !== '' && filter !== this.productFilter) return sum;

          return sum + Number(credit.credits);
        }, 0);
      }

      handleButtonClick(event) {
        event.preventDefault();
        this.showConfirmation();
      }

      handleCancel() {
        this.dialog.close();
      }

      async handleConfirm() {
        try {
          this.confirmBtn.disabled = true;
          this.confirmBtn.textContent = this.i18n.processing;

          const response = await fetch('/apps/credits/redeem', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              variant_id: this.variantId,
              product_id: this.productId,
              credits: this.creditCost,
              filter: this.productFilter,
            }),
          });

          // The backend returns 400 with a usable message when credits fall short,
          // so read the body before treating the status as a failure.
          const result = await response.json().catch(() => null);

          if (!result) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          if (result.success) {
            // Success - show success message with link
            this.showSuccessMessage(this.digitalProductsUrl);
          } else {
            // Show error message
            alert(result.message || this.i18n.errorRedeem);
            this.dialog.close();
            this.resetConfirmButton();
          }
        } catch (error) {
          console.error('[Buy with Credits] Error redeeming credits:', error);
          alert(this.i18n.errorGeneric);
          this.dialog.close();
          this.resetConfirmButton();
        }
      }

      handleBackdropClick(event) {
        if (event.target === this.dialog) {
          this.dialog.close();
        }
      }

      resetConfirmButton() {
        this.confirmBtn.disabled = false;
        this.confirmBtn.textContent = this.confirmBtn.dataset.originalText || 'Yes, Use Credits';
      }

      showConfirmation() {
        const total = Number(this.totalCredits || 0);
        const creditText = total === 1 ? this.i18n.creditSingular : this.i18n.creditPlural;
        const costText = this.creditCost === 1 ? this.i18n.creditSingular : this.i18n.creditPlural;

        // Replace placeholders in confirmation message
        const message = this.i18n.confirmMessage
          .replace('{cost}', this.creditCost)
          .replace('{credit}', costText)
          .replace('{count}', total)
          .replace('{credits}', creditText)
          // Cost can be paid from several credit entries with different filters,
          // so no single filter name applies any more.
          .replace('{filter}', '');

        // confirm_message now contains <span>...</span> so we need HTML
        this.dialogMessage.innerHTML = message;

        if (!this.confirmBtn.dataset.originalText) {
          this.confirmBtn.dataset.originalText = this.confirmBtn.textContent;
        }

        this.dialog.showModal();
      }

      showSuccessMessage(digitalProductsUrl) {
        // Create link element
        const link = document.createElement('a');
        link.href = digitalProductsUrl;
        link.textContent = this.i18n.successLinkText;
        link.className = 'link underlined-link';

        // Clear dialog message and add success message with link
        this.dialogMessage.innerHTML = '';
        const messageText = this.i18n.successMessage.split('{link}');

        if (messageText.length === 2) {
          this.dialogMessage.appendChild(document.createTextNode(messageText[0]));
          this.dialogMessage.appendChild(link);
          this.dialogMessage.appendChild(document.createTextNode(messageText[1]));
        } else {
          this.dialogMessage.textContent = this.i18n.successMessage;
        }

        // Hide confirm button, show only cancel button as "Close"
        this.confirmBtn.style.display = 'none';
        this.cancelBtn.textContent = this.i18n.successClose;

        // Store original cancel text
        if (!this.cancelBtn.dataset.originalText) {
          this.cancelBtn.dataset.originalText = this.cancelBtn.textContent;
        }

        // Reset button state when dialog closes
        this.dialog.addEventListener(
          'close',
          () => {
            this.confirmBtn.style.display = '';
            this.cancelBtn.textContent = this.cancelBtn.dataset.originalText || 'Cancel';
            this.resetConfirmButton();
          },
          { once: true },
        );
      }
    },
  );
}
